// Changes CRM refused for this user (403: the job doesn't open for them, or
// their role doesn't allow it), whose record is no longer in CRM (404
// NOT_IN_CRM), whose version is issued or replaced (409 VERSION_FROZEN), or
// that the API refuses as sent (400; refusal.ts). Sync sets them aside (device-sync.ts
// markRefused) instead of stopping every change behind them; the job's sync
// bar says so and offers:
//  - try again — once Business Systems has sorted out the access: they go
//    back in the queue as they are;
//  - discard — CRM's version comes back (needs signal), a photo's direction
//    included; what CRM has no copy of (a new record, a photo not uploaded) is
//    dropped from this device. Nothing is dropped while it can't tell (discard.ts).
import { getJobById } from './api';
import { takeCrmVersion } from './conflicts';
import { listedDirection } from './area-photos';
import type { Direction } from './compass';
import { assertNoCrmCopy } from './discard';
import { db, notifyChange, queue } from './device-store';
import { forgetArea, forgetPhoto, forgetRecord, listRecords, type DevicePhoto, type DeviceRecord } from './device-db';
import { syncNow } from './outbox';

export interface Refused {
  records: DeviceRecord[];
  photos: DevicePhoto[];
  /** The first reason given, to show. */
  reason: string | null;
}

/** The job's refused changes on this device. */
export async function listRefused(jobId: string): Promise<Refused> {
  const records = (await listRecords(jobId)).filter((r) => r.refusedReason);
  const photos = (await (await db()).getAllFromIndex('photos', 'byJob', jobId)).filter((p) => p.refusedReason);
  return { records, photos, reason: records[0]?.refusedReason ?? photos[0]?.refusedReason ?? null };
}

/** Put them back in the queue, as they are, and sync. */
export async function retryRefused(jobId: string): Promise<void> {
  const { records, photos } = await listRefused(jobId);
  const tx = (await db()).transaction(['records', 'photos', 'outbox'], 'readwrite');
  for (const record of records) {
    await tx.objectStore('records').put({ ...record, state: 'pending', refusedReason: undefined, revision: record.revision + 1 });
    await queue(tx, { kind: 'record', targetId: record.id, jobId });
  }
  for (const photo of photos) {
    await tx.objectStore('photos').put({ ...photo, refusedReason: undefined });
    // A photo still to upload goes as a photo; an uploaded one only had its direction refused.
    await queue(tx, { kind: photo.state === 'waiting' ? 'photo' : 'photo-meta', targetId: photo.id, jobId });
  }
  await tx.done;
  notifyChange();
  void syncNow();
}

/**
 * CRM's copy of the job's records — none when the job no longer opens for this
 * user (the usual reason for a refusal). Throws, before anything is discarded,
 * when it can't tell: the user may no longer use the app, or CRM is unreachable (discard.ts).
 */
async function crmRecords(jobId: string) {
  try {
    return (await getJobById(jobId)).records;
  } catch (err) {
    assertNoCrmCopy(err);
    return [];
  }
}

/** Give them up: CRM's version where it has one, else drop them from this device. Needs signal. */
export async function discardRefused(jobId: string): Promise<void> {
  const { records, photos } = await listRefused(jobId);
  const crm = await crmRecords(jobId);
  for (const record of records) {
    const theirs = crm.find((r) => r.id === record.id);
    if (theirs) await takeCrmVersion(record, { kind: 'changed', record: theirs, changes: [] });
    else if (record.table === 'area') await forgetArea(record.id);
    else await forgetRecord(record.id);
  }
  const store = await db();
  for (const photo of photos) {
    // An uploaded photo only had its new direction refused: CRM's direction comes back (its area's photo list).
    // With no CRM entry the device's copy is only an overlay of a file already on SharePoint, so it goes.
    const area = crm.find((r) => r.id === photo.areaId);
    const theirs = photo.state === 'uploaded' && area ? listedDirection(area.fields.hig_photos, photo.id) : undefined;
    if (theirs !== undefined) await store.put('photos', { ...photo, direction: theirs as Direction | null, refusedReason: undefined });
    else await forgetPhoto(photo.id);
  }
  notifyChange();
}
